import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ChevronRight, Folder, Settings as SettingsIcon, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentOrganization } from '../contexts/OrganizationContext';
import { useOrganizations } from '../hooks/useOrganizations';
import { useProjects } from '../hooks/useProjects';
import { Badge, EmptyState } from '../ui';
import './dashboard.css';

export function DashboardPage() {
  const { user } = useAuth();
  const { orgId } = useParams<{ orgId?: string }>();
  const { currentOrg, currentOrgId, isAdmin, setCurrentOrgId } = useCurrentOrganization();
  const { data: organizations, isLoading: orgsLoading } = useOrganizations();
  const { data: projects, isLoading: projectsLoading } = useProjects(orgId);

  useEffect(() => {
    if (orgId && orgId !== currentOrgId) {
      setCurrentOrgId(orgId);
    }
  }, [orgId, currentOrgId, setCurrentOrgId]);

  const atRoot = !orgId;
  if (atRoot && currentOrgId && organizations?.some(o => o.id === currentOrgId)) {
    return <Navigate to={`/organizations/${currentOrgId}`} replace />;
  }

  if (orgId && currentOrg) {
    return (
      <div className="dashboard">
        <header className="dashboard__header">
          <h1>{currentOrg.name}</h1>
          <Badge tone="info">{currentOrg.role}</Badge>
        </header>

        <section className="dashboard__section">
          <div className="dashboard__section-header">
            <h3>Projects</h3>
            <Link to={`/organizations/${orgId}/projects`} className="dashboard__link">
              View all
            </Link>
          </div>

          {projectsLoading ? (
            <div className="dashboard__loading">Loading projects...</div>
          ) : projects && projects.length > 0 ? (
            <div className="dashboard__project-grid">
              {projects.slice(0, 6).map(project => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="dashboard__project-tile"
                >
                  <span className="dashboard__project-icon" aria-hidden="true">
                    <Folder size={18} />
                  </span>
                  <span className="dashboard__project-text">
                    <span className="dashboard__project-name">{project.name}</span>
                    <span className="dashboard__project-slug">{project.slug}</span>
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Folder size={20} />}
              title="No projects yet."
              description="Create your first project to start configuring authentication."
              action={
                <Link
                  to={`/organizations/${orgId}/projects`}
                  className="ui-button ui-button--primary ui-button--md"
                >
                  Create your first project
                </Link>
              }
            />
          )}
        </section>

        <div className="dashboard__tiles">
          <Link to={`/organizations/${orgId}/members`} className="dashboard__tile">
            <span className="dashboard__tile-icon" aria-hidden="true">
              <Users size={20} />
            </span>
            <div className="dashboard__tile-text">
              <h3>Members</h3>
              <p>Manage team members and roles</p>
            </div>
            <ChevronRight size={18} className="dashboard__tile-chevron" aria-hidden="true" />
          </Link>

          {isAdmin && (
            <Link to={`/organizations/${orgId}/settings`} className="dashboard__tile">
              <span className="dashboard__tile-icon" aria-hidden="true">
                <SettingsIcon size={20} />
              </span>
              <div className="dashboard__tile-text">
                <h3>Settings</h3>
                <p>Organization settings and configuration</p>
              </div>
              <ChevronRight size={18} className="dashboard__tile-chevron" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard__header">
        <h1>Dashboard</h1>
      </header>

      <section className="dashboard__section">
        <div className="dashboard__welcome">
          <h2>Welcome back, {user?.name}</h2>
          <p>Manage your organizations and team members from here.</p>
        </div>
      </section>

      <section className="dashboard__section">
        <div className="dashboard__section-header">
          <h3>Your organizations</h3>
          <Link to="/organizations" className="dashboard__link">
            View all
          </Link>
        </div>

        {orgsLoading ? (
          <div className="dashboard__loading">Loading organizations...</div>
        ) : organizations && organizations.length > 0 ? (
          <ul className="dashboard__org-list">
            {organizations.slice(0, 4).map(org => (
              <li key={org.id}>
                <Link to={`/organizations/${org.id}`} className="dashboard__org-item">
                  <span className="dashboard__org-avatar" aria-hidden="true">
                    {org.logo_url ? (
                      <img src={org.logo_url} alt="" />
                    ) : (
                      <span>{org.name.charAt(0).toUpperCase()}</span>
                    )}
                  </span>
                  <span className="dashboard__org-text">
                    <span className="dashboard__org-name">{org.name}</span>
                    <Badge tone="neutral">{org.role}</Badge>
                  </span>
                  <ChevronRight size={16} className="dashboard__org-chevron" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<Users size={20} />}
            title="You don't belong to any organizations yet."
            action={
              <Link to="/organizations" className="ui-button ui-button--primary ui-button--md">
                Create your first organization
              </Link>
            }
          />
        )}
      </section>
    </div>
  );
}
